import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { JdcManagerService } from '../jdc-manager.service';
import { AppService } from '../../../../app.service';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';

@Component({
    selector: 'gtw-jdc-new-key-modal',
    templateUrl: './jdc-new-key-modal.component.html',
    styleUrls: ['./jdc-new-key-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class JdcNewKeyModalComponent implements OnChanges, OnDestroy {
  @Input() mode: 'new' | 'edit' = 'new';
  @Input() jcdData: any;
  @Input() clientKey: any;
  @Input() client: any;
  @Input() channelList: any[] = [];
  @Input() jcdCombiList: any[] = [];

  @Output() saved = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  jcdModalMinimise = false;
  jcdModalClient: any = null;
  jcdModalChannel: any = null;
  jcdModalClientList: any[] = [];
  jcdModalChannelList: any[] = [];
  jcdModalChannelLoading = false;
  jcdModalShowNewChannel = false;
  jcdNewChannelForm: any = { client: null, channel_name: '', channel_desc: '', channel_priority: 1, channel_status: 'A' };

  channelStatusList = [
    { name: 'Active', value: 'A' },
    { name: 'Inactive', value: 'I' },
  ];

  private pendingChannelKey: number | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private jdcManagerService: JdcManagerService,
    private appService: AppService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(): void {
    const userSettings = this.appService.USER_SETTINGS;
    this.jcdModalClientList = userSettings?.user?.clients || [];

    if (this.mode === 'edit' && this.jcdData) {
      this.jcdModalClient = this.jcdModalClientList.find(
        (c: any) => c.client_key === this.jcdData.client_key
      ) || this.jcdModalClientList[0] || null;
      this.jcdModalMinimise = false;
      this.jcdModalShowNewChannel = false;
      this.pendingChannelKey = this.jcdData.channel_key;
      this.onJcdModalClientChange();
    } else {
      this.jcdModalClient = this.jcdModalClientList.find(
        (c: any) => c.client_key === this.client?.client_key
      ) || this.jcdModalClientList[0] || null;
      this.jcdModalChannel = null;
      this.jcdModalMinimise = false;
      this.jcdModalShowNewChannel = false;
      this.pendingChannelKey = null;
      this.jcdNewChannelForm = { client: this.jcdModalClient, channel_name: '', channel_desc: '', channel_priority: 1, channel_status: 'A' };
      this.onJcdModalClientChange();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  close(): void {
    this.closed.emit();
  }

  onJcdModalClientChange(): void {
    if (!this.jcdModalClient) return;
    this.jcdModalChannelLoading = true;
    this.cdr.detectChanges();
    this.jdcManagerService.getInitInfo({ client: { value: this.jcdModalClient.client_key } })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const data = res?.jcdManage ?? res?.data?.jcdManage ?? res;
          this.jcdModalChannelList = data?.clientChannelList ?? data?.channelList ?? [];
          this.jcdModalChannelLoading = false;
          if (this.pendingChannelKey != null) {
            this.jcdModalChannel = this.jcdModalChannelList.find(
              (ch: any) => ch.channel_key === this.pendingChannelKey
            ) || null;
            this.pendingChannelKey = null;
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.jcdModalChannelLoading = false;
          this.pendingChannelKey = null;
          this.cdr.detectChanges();
        }
      });
  }

  clearJcdModalForm(): void {
    this.jcdModalChannel = null;
    this.jcdModalShowNewChannel = false;
    this.jcdNewChannelForm = { client: this.jcdModalClient, channel_name: '', channel_desc: '', channel_priority: 1, channel_status: 'A' };
    this.cdr.detectChanges();
  }

  saveJcdModalChannel(): void {
    const form = this.jcdNewChannelForm;
    if (!form.channel_name) {
      this.gtwSnackbarService.openSnackBar('Channel Name is required.', 'gtw-snackbar--red');
      return;
    }
    const ck = form.client?.client_key ?? this.jcdModalClient?.client_key;
    const payload = [{
      CHANNEL_NAME: form.channel_name,
      CHANNEL_DESC: form.channel_desc || '',
      CHANNEL_PRIORITY: form.channel_priority || 1,
      CHANNEL_STATUS: form.channel_status || 'A',
      CLIENT_KEY: ck,
    }];
    this.jdcManagerService.saveJsonAction('nhuasw', payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (String(res?.callSuccess) === '1') {
            this.gtwSnackbarService.openSnackBar('Channel saved successfully.', 'gtw-snackbar--green');
            this.jcdModalShowNewChannel = false;
            this.onJcdModalClientChange();
          } else {
            this.gtwSnackbarService.openSnackBar('Problem saving channel.', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.gtwSnackbarService.openSnackBar('Problem saving channel.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      });
  }

  saveNewJCD(): void {
    if (!this.jcdModalClient || !this.jcdModalChannel) {
      this.gtwSnackbarService.openSnackBar('Please select Client and App Key.', 'gtw-snackbar--red');
      return;
    }
    if (this.jcdCombiList.find((j: any) =>
      j.channel_key === this.jcdModalChannel.channel_key && j.client_key === this.jcdModalClient.client_key
    )) {
      this.gtwSnackbarService.openSnackBar('JCD Code already exists!', 'gtw-snackbar--red');
      return;
    }
    this.jdcManagerService.addNewJcd({
      client_key: this.jcdModalClient.client_key, jurisdiction_key: 1,
      domain_key: 9, channel_key: this.jcdModalChannel.channel_key
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        if ((res?.result?.value ?? res?.data?.result?.value) === '1') {
          this.gtwSnackbarService.openSnackBar('JCD saved successfully.', 'gtw-snackbar--green');
          this.saved.emit();
        } else {
          this.gtwSnackbarService.openSnackBar('Problem saving new JCD.', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.gtwSnackbarService.openSnackBar('Problem saving new JCD.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    });
  }
}
